import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useMenuStore } from '../../../zustand/store/useMenuStore';

/**
 * @공통NewCommonSelect 해당 컴포넌트로 넘기는 props
 * (컴포넌트는 꼭 tr태그로 감싸주어야 함 - <tr>를 쓰지 않으면 hydration error 발생)
    <tr>
        <NewCommonSelect
            hospitalTF={false}
            searchParams={searchParams}
            handleInputChange={handleInputChange}
        />
    </tr>
 * @hospitalTF 기관명 유무에 따라 false, true로 넘겨주기
    false: 센터구분, 지역
    true: 센터구분, 지역, 기관명
 * @searchParams 넘겨줄 (VO에 정의된) 기본 parameter
  <sample>
   -- 공통으로 사용하는 parameter(기관구분, 지역, 기관명, 시작일자, 종료일자 등)는 변수 그대로 사용하기
    const [searchParams, setSearchParams] = useState({
        searchFromdate: new Date((new Date()).getTime() + 1000 * 60 * 60 * 9).toISOString().split('T')[0],
        searchTodate: new Date((new Date()).getTime() + 1000 * 60 * 60 * 9).toISOString().split('T')[0],
        searchEmog: '',
        searchGrdc: '',
        searchLoca: '',
    });
 * @handleInputChange input value가 바뀔 때 마다 변경된 parameter로 저장하는 함수
  <sample>
   -- 필요한 input value는 이 함수 안에 모두 작성
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newParams = {
            ...searchParams,
            [name]: type === 'checkbox' ? (checked ? "1" : "") : value
        };
        setSearchParams(newParams);
    };
 * @exValidation searchBtn component가 아닌 검색버튼을 따로 사용하여 validation check를 따로 하는 경우 추가(선택)
    -- 기관명이 필수인 경우 기관명 옆에 * 표시를 하여 UX향상을 위함
 * @lockOwnOrg J003 사용자는 센터구분 · 지역 · 기관명을 고르지 못하게 막을지(기본 true). SearchBtn 에 넘긴 값과 맞춘다
 */
const NewCommonSelect = ({ hospitalTF, searchParams, handleInputChange, exValidation, lockOwnOrg = true }) => {

    const ownOrgLocked = lockOwnOrg && localStorage.getItem("userAuth") === 'J003';

    const clickedCategory = useMenuStore((state) => state.clickedCategory);
    const trMenu = clickedCategory === 21;

    const [emogList, setEmogList] = useState([]);
    const [grdcList, setGrdcList] = useState([]);
    const [locaList, setLocaList] = useState([]);

    const ptmiListApi = '/ptmiapi/listInfo.do'
    const ptmiEmogApi = '/ptmiapi/emogList.do'
    const trmsListApi = '/ptmiapi/trmsListInfo.do'
    const trmsEmogApi = '/ptmiapi/trmsEmogList.do'

    const listApiCall = trMenu ? trmsListApi : ptmiListApi;
    const emogApiCall = trMenu ? trmsEmogApi : ptmiEmogApi;

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const response = await axios.get(listApiCall);
                if(trMenu) {
                    setLocaList(response.data.optionList || []);
                } else {
                    setGrdcList(response.data.optionList.grdc || []);
                    setLocaList(response.data.optionList.loca || []);
                }
                const response2 = await axios.get(emogApiCall, {
                    params: {
                        grdc: searchParams.searchGrdc,
                        loca: searchParams.searchLoca
                    }
                });
                setEmogList(response2.data.emogList || []);
            } catch (error) {
                console.error('초기 데이터 로딩 중 오류 발생:', error);
            }
        };
        fetchInitialData();
    }, []);

    const mounted = useRef(false);

    useEffect(() => {
        if (hospitalTF) {
            const fetchHospList = async () => {
                if (!mounted.current) {
                    mounted.current = true;
                } else {
                    try {
                        const response = await axios.get(emogApiCall, {
                            params: {
                                grdc: searchParams.searchGrdc,
                                loca: searchParams.searchLoca
                            }
                        });
                        setEmogList(response.data.emogList || []);
                    } catch (error) {
                        console.error('병원 목록 로딩 중 오류 발생:', error);
                    }
                }
            };
            fetchHospList();
        }
    }, [searchParams.searchGrdc, searchParams.searchLoca]);

    const valiCheckNon = !exValidation && (searchParams?.validationCheckList === undefined || !searchParams?.validationCheckList?.includes('searchEmog'));
    
    return (
        <>
            <th>센터구분</th>
            <td>
                <select
                    name='searchGrdc'
                    className='w-full'
                    value={searchParams.searchGrdc}
                    onChange={handleInputChange}
                    disabled={ownOrgLocked}
                >
                    <option value="">전체</option>
                    {trMenu ?
                        <>
                            <option value="Y">권역외상센터</option>
                            <option value="N">외상수련기관</option>
                        </>
                    :
                        grdcList.map(grdc => (
                            <option key={grdc.bscdminr} value={grdc.bscdminr}>
                                {grdc.bscddesc}
                            </option>
                        ))
                    }
                </select>
            </td>
            <th>지역{!searchParams?.validationCheckList?.includes('searchLoca') ? '' : <span className='co_red'>&nbsp;*</span>}</th>
            <td>
                <select
                    name='searchLoca'
                    className='w-full'
                    value={searchParams.searchLoca}
                    onChange={handleInputChange}
                    disabled={ownOrgLocked}
                >
                    <option value="">전체</option>
                    {locaList && locaList.map(loca => (
                        <option key={loca.bscdminr} value={loca.bscdminr}>
                            {loca.bscddesc}
                        </option>
                    ))}
                </select>
            </td>
            {!hospitalTF ? <></> :
                <>
                    <th>기관명{valiCheckNon ? '' : <span className='co_red'>&nbsp;*</span>}</th>
                    <td>
                        <select
                            name='searchEmog'
                            className='w-full'
                            value={searchParams.searchEmog}
                            onChange={handleInputChange}
                            disabled={ownOrgLocked}
                        >
                            {ownOrgLocked ?
                                <option value={localStorage.getItem("useremcd")}>{localStorage.getItem("emogdesc")}</option>
                                :
                                <>
                                    <option value="">전체</option>
                                    {emogList.map(hosp => (
                                        <option key={hosp.EMOGCODE || hosp.emogcode} value={hosp.EMOGCODE || hosp.emogcode}>
                                            {hosp.EMOGDESC || hosp.emogdesc}
                                        </option>
                                    ))}
                                </>
                            }
                        </select>
                    </td>
                </>
            }
        </>
    );
};

export default NewCommonSelect; 